import { useState, useEffect } from 'react'
import { Calendar, Plus, Edit, TrendingUp, Beef } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { Button, Card } from '@/components/common'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import FeedingFormModal from './FeedingFormModal'
import { API_BASE_URL, API_ENDPOINTS } from '@/constants'
import { storageService } from '@/services'

// Cattle types
const CATTLE_TYPES = [
  { value: 'BREEDING_COW', label: 'แม่พันธุ์' },
  { value: 'PREGNANT_COW', label: 'แม่ท้อง' },
  { value: 'BREEDING_BULL', label: 'พ่อพันธุ์' },
  { value: 'FATTENING_CATTLE', label: 'โคขุน' },
  { value: 'CALF', label: 'ลูกโค' },
  { value: 'YOUNG_CATTLE', label: 'โครุ่น' },
]

interface FeedingRecord {
  id: string
  date: string
  cattleType: string
  roughage: { weight: number; dryMatter: number }
  concentrate: { weight: number; dryMatter: number }
  tmr: { weight: number; dryMatter: number }
  notes?: string
}

type DateRange = '7' | '14' | '30' | '90' | 'custom'

export default function FeedingPage() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedCattleType, setSelectedCattleType] = useState<string>('BREEDING_COW')
  const [showModal, setShowModal] = useState(false)
  const [editRecord, setEditRecord] = useState<FeedingRecord | null>(null)
  const [dateRange, setDateRange] = useState<DateRange>('7')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [allFeedingRecords, setAllFeedingRecords] = useState<FeedingRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [farmId, setFarmId] = useState<string>('')

  // Generate chart data based on selected date range
  const getChartData = () => {
    // Dummy data for chart - น้ำหนักแห้ง (DM) ต่อวันแยกตามกลุ่มวัว
    const daysToShow = dateRange === 'custom' ? 7 : parseInt(dateRange)

    // Sample data for demonstration
    const sampleData = [
      { date: '7 พ.ย.', BREEDING_COW: 66.75, PREGNANT_COW: 73, BREEDING_BULL: 55, FATTENING_CATTLE: 77.25, CALF: 17.05, YOUNG_CATTLE: 45 },
      { date: '8 พ.ย.', BREEDING_COW: 68, PREGNANT_COW: 74, BREEDING_BULL: 56, FATTENING_CATTLE: 78, CALF: 17.5, YOUNG_CATTLE: 46 },
      { date: '9 พ.ย.', BREEDING_COW: 65, PREGNANT_COW: 72, BREEDING_BULL: 54, FATTENING_CATTLE: 76, CALF: 16.8, YOUNG_CATTLE: 44 },
      { date: '10 พ.ย.', BREEDING_COW: 70, PREGNANT_COW: 75, BREEDING_BULL: 57, FATTENING_CATTLE: 79, CALF: 18, YOUNG_CATTLE: 47 },
      { date: '11 พ.ย.', BREEDING_COW: 67, PREGNANT_COW: 73.5, BREEDING_BULL: 55.5, FATTENING_CATTLE: 77.5, CALF: 17.2, YOUNG_CATTLE: 45.5 },
      { date: '12 พ.ย.', BREEDING_COW: 69, PREGNANT_COW: 74.5, BREEDING_BULL: 56.5, FATTENING_CATTLE: 78.5, CALF: 17.8, YOUNG_CATTLE: 46.5 },
      { date: '13 พ.ย.', BREEDING_COW: 71, PREGNANT_COW: 76, BREEDING_BULL: 58, FATTENING_CATTLE: 80, CALF: 18.5, YOUNG_CATTLE: 48 },
      // Additional data for longer periods
      { date: '14 พ.ย.', BREEDING_COW: 68.5, PREGNANT_COW: 74.2, BREEDING_BULL: 56.2, FATTENING_CATTLE: 78.2, CALF: 17.6, YOUNG_CATTLE: 46.2 },
      { date: '15 พ.ย.', BREEDING_COW: 69.5, PREGNANT_COW: 75.2, BREEDING_BULL: 57.2, FATTENING_CATTLE: 79.2, CALF: 18.2, YOUNG_CATTLE: 47.2 },
      { date: '16 พ.ย.', BREEDING_COW: 70.5, PREGNANT_COW: 76.2, BREEDING_BULL: 58.2, FATTENING_CATTLE: 80.2, CALF: 18.8, YOUNG_CATTLE: 48.2 },
    ]

    // Return data based on selected range
    return sampleData.slice(Math.max(0, sampleData.length - daysToShow))
  }

  const chartData = getChartData()

  // Fetch user's farm ID
  useEffect(() => {
    const fetchUserFarms = async () => {
      try {
        const token = storageService.getAuthToken()

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMS.BASE}`, {
          headers: { Authorization: `Bearer ${token}` },
        })


        if (response.ok) {
          const data = await response.json()

          // Check different response structures
          if (data && Array.isArray(data)) {
            // If data is directly an array
            if (data.length > 0) {
              setFarmId(data[0].id)
            } else {
              console.warn('Farms array is empty')
              alert('ไม่พบข้อมูลฟาร์ม กรุณาสร้างฟาร์มก่อน')
            }
          } else if (data && data.data && Array.isArray(data.data)) {
            // If data is wrapped in { data: [...] }
            if (data.data.length > 0) {
              setFarmId(data.data[0].id)
            } else {
              console.warn('Farms data.data is empty')
              alert('ไม่พบข้อมูลฟาร์ม กรุณาสร้างฟาร์มก่อน')
            }
          } else {
            console.error('Unexpected farms response structure:', data)
            alert('รูปแบบข้อมูลฟาร์มไม่ถูกต้อง')
          }
        } else {
          console.error('Failed to fetch farms, status:', response.status)
          const errorText = await response.text()
          console.error('Error response:', errorText)
          alert('ไม่สามารถดึงข้อมูลฟาร์มได้')
        }
      } catch (error) {
        console.error('Error fetching farms:', error)
        alert('เกิดข้อผิดพลาดในการดึงข้อมูลฟาร์ม')
      }
    }
    fetchUserFarms()
  }, [])

  // Fetch feeding records
  useEffect(() => {
    if (!farmId) return
    fetchFeedingRecords()
  }, [selectedDate, farmId])

  const fetchFeedingRecords = async () => {
    try {
      setIsLoading(true)
      const token = storageService.getAuthToken()
      const dateStr = selectedDate.toISOString().split('T')[0]

      const url = `${API_BASE_URL}${API_ENDPOINTS.FEEDING.BASE}?farmId=${farmId}&date=${dateStr}`

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })


      if (response.ok) {
        const data = await response.json()

        // Transform API data to match component format
        const records = data.map((record: any) => ({
          id: record.id,
          date: record.date.split('T')[0],
          cattleType: record.cattleType,
          roughage: { weight: record.roughageWeight, dryMatter: record.roughageDryMatter },
          concentrate: { weight: record.concentrateWeight, dryMatter: record.concentrateDryMatter },
          tmr: { weight: record.tmrWeight, dryMatter: record.tmrDryMatter },
          notes: record.notes || '',
        }))

        setAllFeedingRecords(records)
      } else {
        console.error('Failed to fetch feeding records, status:', response.status)
        const errorText = await response.text()
        console.error('Error response:', errorText)
      }
    } catch (error) {
      console.error('Error fetching feeding records:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Filter records by selected date and cattle type
  const dateStr = selectedDate.toISOString().split('T')[0]
  const feedingRecords = allFeedingRecords.filter(
    (record) => record.date === dateStr && record.cattleType === selectedCattleType
  )

  const handleAddNew = () => {
    // ตรวจสอบว่ามีข้อมูลของวันนี้และกลุ่มวัวที่เลือกหรือยัง
    const dateStr = selectedDate.toISOString().split('T')[0]
    const existingRecord = allFeedingRecords.find(
      (record) => record.date === dateStr && record.cattleType === selectedCattleType
    )

    if (existingRecord) {
      // ถ้ามีอยู่แล้ว ให้เปิดฟอร์มแบบแก้ไข
      setEditRecord(existingRecord)
    } else {
      // ถ้าไม่มี ให้เปิดฟอร์มเปล่า
      setEditRecord(null)
    }
    setShowModal(true)
  }

  const handleEdit = (record: FeedingRecord) => {
    setEditRecord(record)
    setShowModal(true)
  }

  const handleSave = async (data: FeedingRecord, _formDate: Date) => {
    try {

      // Validate required fields
      if (!farmId || !data.date || !data.cattleType) {
        console.error('Missing required fields:', { farmId, date: data.date, cattleType: data.cattleType })

        if (!farmId) {
          alert('ไม่พบข้อมูลฟาร์ม กรุณารีเฟรชหน้าเว็บ')
        } else if (!data.date) {
          alert('ไม่พบข้อมูลวันที่')
        } else if (!data.cattleType) {
          alert('กรุณาเลือกกลุ่มวัว')
        }
        return
      }

      const token = storageService.getAuthToken()

      // Prepare payload - ensure date is in correct format
      const payload = {
        farmId: farmId.trim(),
        date: data.date,
        cattleType: data.cattleType.trim(),
        roughageWeight: data.roughage.weight || 0,
        roughageDryMatter: data.roughage.dryMatter || 0,
        concentrateWeight: data.concentrate.weight || 0,
        concentrateDryMatter: data.concentrate.dryMatter || 0,
        tmrWeight: data.tmr.weight || 0,
        tmrDryMatter: data.tmr.dryMatter || 0,
        notes: data.notes || '',
      }


      // Create or update via API
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FEEDING.CREATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        await response.json()

        // Refresh data
        await fetchFeedingRecords()

        setShowModal(false)
        setEditRecord(null)
        alert('บันทึกข้อมูลสำเร็จ')
      } else {
        const error = await response.json()
        console.error('API Error Response:', error)
        alert(`เกิดข้อผิดพลาด: ${error.message || 'ไม่สามารถบันทึกข้อมูลได้'}`)
      }
    } catch (error) {
      console.error('Error saving feeding record:', error)
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
    }
  }

  const getCattleTypeLabel = (type: string) => {
    return CATTLE_TYPES.find((t) => t.value === type)?.label || type
  }

  const calculateTotalWeight = (record: FeedingRecord) => {
    return record.roughage.weight + record.concentrate.weight + record.tmr.weight
  }

  const calculateTotalDryMatter = (record: FeedingRecord) => {
    const roughageDM = (record.roughage.weight * record.roughage.dryMatter) / 100
    const concentrateDM = (record.concentrate.weight * record.concentrate.dryMatter) / 100
    const tmrDM = (record.tmr.weight * record.tmr.dryMatter) / 100
    return roughageDM + concentrateDM + tmrDM
  }

  // Calculate stats for selected date only
  const recordsForDate = allFeedingRecords.filter((record) => record.date === dateStr)
  const totalRecords = recordsForDate.length
  const totalWeight = recordsForDate.reduce((sum, r) => sum + calculateTotalWeight(r), 0)
  const totalDryMatter = recordsForDate.reduce((sum, r) => sum + calculateTotalDryMatter(r), 0)

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
              การบันทึกอาหาร
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              บันทึกและติดตามการให้อาหารแต่ละกลุ่มวัว
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleAddNew}
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>บันทึกการให้อาหาร</span>
          </Button>
        </div>

        {/* Graph Card */}
        <Card>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-800" />
              <h2 className="text-lg font-semibold text-text-primary">
                กราฟน้ำหนักแห้ง (DM) ต่อวันแยกตามกลุ่มวัว
              </h2>
            </div>

            {/* Date Range Selector */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setDateRange('7')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '7'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                7 วัน
              </button>
              <button
                onClick={() => setDateRange('14')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '14'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                14 วัน
              </button>
              <button
                onClick={() => setDateRange('30')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '30'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                1 เดือน
              </button>
              <button
                onClick={() => setDateRange('90')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === '90'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                3 เดือน
              </button>
              <button
                onClick={() => setDateRange('custom')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === 'custom'
                    ? 'bg-primary-800 text-white'
                    : 'bg-background-tertiary text-text-secondary hover:bg-primary-100'
                }`}
              >
                กำหนดเอง
              </button>
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {dateRange === 'custom' && (
            <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
              <label className="text-sm font-medium text-text-primary">ช่วงเวลา:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-1.5 border border-border-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-800"
                placeholder="วันเริ่มต้น"
              />
              <span className="text-text-secondary">ถึง</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-1.5 border border-border-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-800"
                placeholder="วันสิ้นสุด"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  // TODO: Fetch data for custom date range
                }}
              >
                แสดงข้อมูล
              </Button>
            </div>
          )}

          {chartData && chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis label={{ value: 'น้ำหนักแห้ง (กก.)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="BREEDING_COW"
                  stroke="#ec4899"
                  name="แม่พันธุ์"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="PREGNANT_COW"
                  stroke="#f97316"
                  name="แม่ท้อง"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="BREEDING_BULL"
                  stroke="#3b82f6"
                  name="พ่อพันธุ์"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="FATTENING_CATTLE"
                  stroke="#10b981"
                  name="โคขุน"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="CALF"
                  stroke="#a855f7"
                  name="ลูกโค"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="YOUNG_CATTLE"
                  stroke="#f59e0b"
                  name="โครุ่น"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[350px]">
              <p className="text-text-tertiary">ไม่มีข้อมูลสำหรับแสดงกราฟ</p>
            </div>
          )}
        </Card>

        {/* Calendar & Records */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Calendar Sidebar */}
          <div className="lg:col-span-3">
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-primary-800" />
                <h3 className="font-semibold text-text-primary">เลือกวันที่</h3>
              </div>
              <div className="space-y-2">
                <input
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  className="w-full px-3 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800"
                />
                <div className="text-sm text-text-secondary mt-4">
                  <p className="font-medium mb-2">วันที่เลือก:</p>
                  <p className="text-lg font-semibold text-primary-800">
                    {selectedDate.toLocaleDateString('th-TH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </Card>

            {/* Summary Stats */}
            <Card className="mt-4">
              <div className="flex items-center gap-2 mb-4">
                <Beef className="w-5 h-5 text-primary-800" />
                <h3 className="font-semibold text-text-primary">สรุปวันนี้</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-text-tertiary">กลุ่มวัว</p>
                  <p className="text-xl font-bold text-text-primary">{totalRecords}</p>
                </div>
                <div>
                  <p className="text-xs text-text-tertiary">น้ำหนักรวม</p>
                  <p className="text-xl font-bold text-green-600">{totalWeight} กก.</p>
                </div>
                <div>
                  <p className="text-xs text-text-tertiary">น้ำหนักแห้งรวม</p>
                  <p className="text-xl font-bold text-amber-600">
                    {totalDryMatter.toFixed(1)} กก.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Records Table with Tabs */}
          <div className="lg:col-span-9">
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-text-primary">
                  รายการการให้อาหาร
                </h2>
                <span className="text-sm text-text-tertiary">
                  {selectedDate.toLocaleDateString('th-TH')}
                </span>
              </div>

              {/* Cattle Type Tabs */}
              <div className="flex gap-2 border-b border-border-light mb-6 overflow-x-auto">
                {CATTLE_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedCattleType(type.value)}
                    className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                      selectedCattleType === type.value
                        ? 'text-primary-800 border-b-2 border-primary-800'
                        : 'text-text-tertiary hover:text-text-secondary'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <p className="text-text-secondary">กำลังโหลดข้อมูล...</p>
                </div>
              ) : feedingRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-light">
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          อาหารหยาบ
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          อาหารข้น
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          TMR
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          รวม (กก.)
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          น้ำหนักแห้ง (กก.)
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          หมายเหตุ
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-text-secondary">
                          จัดการ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedingRecords.map((record) => (
                        <tr
                          key={record.id}
                          className="border-b border-border-light hover:bg-background-secondary transition-colors"
                        >
                          <td className="py-4 px-4 text-center">
                            <p className="text-sm text-text-primary">
                              {record.roughage.weight} กก.
                            </p>
                            <p className="text-xs text-text-tertiary">
                              DM: {record.roughage.dryMatter}%
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <p className="text-sm text-text-primary">
                              {record.concentrate.weight} กก.
                            </p>
                            <p className="text-xs text-text-tertiary">
                              DM: {record.concentrate.dryMatter}%
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <p className="text-sm text-text-primary">{record.tmr.weight} กก.</p>
                            <p className="text-xs text-text-tertiary">
                              DM: {record.tmr.dryMatter}%
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <p className="text-sm font-semibold text-green-600">
                              {calculateTotalWeight(record)}
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <p className="text-sm font-semibold text-amber-600">
                              {calculateTotalDryMatter(record).toFixed(1)}
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <p className="text-xs text-text-tertiary">
                              {record.notes || '-'}
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleEdit(record)}
                              className="flex items-center gap-1 mx-auto"
                            >
                              <Edit className="w-4 h-4" />
                              <span>แก้ไข</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-background-tertiary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Beef className="w-8 h-8 text-text-tertiary" />
                  </div>
                  <p className="text-text-secondary mb-2">
                    ยังไม่มีข้อมูลสำหรับ {getCattleTypeLabel(selectedCattleType)}
                  </p>
                  <p className="text-sm text-text-tertiary mb-4">
                    เริ่มบันทึกการให้อาหารสำหรับกลุ่มนี้
                  </p>
                  <Button variant="primary" onClick={handleAddNew}>
                    <Plus className="w-5 h-5 mr-2" />
                    บันทึกการให้อาหาร
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Feeding Form Modal */}
      {showModal && (
        <FeedingFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          selectedDate={selectedDate}
          editData={editRecord}
          defaultCattleType={selectedCattleType}
          farmId={farmId}
        />
      )}
    </MainLayout>
  )
}
